import { useCallback, useEffect, useMemo, useState } from "react";
import {
  generatePath,
  useNavigate,
  useOutlet,
  useParams,
} from "react-router-dom";
import ProjectCard, {
  PROJECT_CARD_DESKTOP_MAX_WIDTH,
  PROJECT_CARD_DESKTOP_MIN_WIDTH,
} from "../../components/ProjectCard/ProjectCard";

import { useTranslation } from "react-i18next";
import TeamTopSection from "./TeamTopSection";

import { Skeleton, Typography } from "@mui/material";
import { Box, ContentRepeater } from "../../common/components";
import GradientButton from "../../common/components/Button/GradientButton";
import { usePageTitle } from "../../common/hooks/usePageTitle";
import { useSelector } from "../../common/hooks/useSelector";
import {
  DashboardToolbarDesktopHeight,
  DashboardToolbarMobileHeight,
} from "../../components/AppToolbar/AppToolbar";
import InviteTeammateModal from "../../components/InviteTeammateModal/InviteTeammateModal";
import ProjectCardSkeleton from "../../components/ProjectCard/ProjectCardSkeleton";
import ProjectService from "../../service/ProjectService";
import { createStyles, spacing } from "../../styles/theme";
import { ROUTES } from "../../constant/common.constant";
import { useGlobalLoading } from "../../utils/ContextHooks";
import { canDeleteUser, isCurrentUser } from "../../utils/teamUtils";
import { getScreenHeightMinusSpacing } from "../../utils/themeUtils";
import { Teammate, TeammateBlankState } from "./Teammate";
import TeammateSkeleton from "./Teammate/TeammateSkeleton";
import { useAppLoading } from "../../context/AppLoading/useAppLoading";
import { useTeam } from "../../context/Team";
import TemplateService from "../../service/template.service";
import { toast } from "react-toastify";

const styles = createStyles({
  container: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    maxWidth: "100%",
    overflowY: "auto",
    overflowX: "hidden",
    maxHeight: {
      xs: getScreenHeightMinusSpacing(DashboardToolbarMobileHeight),
      lg: getScreenHeightMinusSpacing(DashboardToolbarDesktopHeight),
    },
  },
  leftContainer: {
    pt: { xs: spacing.wrapperMobile, lg: spacing.regularDesktop },
    pl: { xs: spacing.wrapperMobile, lg: spacing.largeDesktop },
    pr: { xs: spacing.wrapperMobile, lg: spacing.largeDesktop },
    width: "100%",
  },
  projectName: {
    mb: { xs: spacing.groupingMobile, lg: spacing.groupingDesktop },
  },
  buttonContainer: {
    mb: { xs: spacing.largeMobile, lg: spacing.largeDesktop },
    gap: { xs: spacing.ctaSpacingMobile, lg: spacing.ctaSpacingDesktop },
    display: "flex",
    alignItems: "center",
  },
  projectContainer: {
    pb: { xs: spacing.xlMobile, lg: spacing.xlDesktop },
    display: "grid",
    gridTemplateColumns: {
      xs: `1fr`,
      md: `repeat(auto-fill, minmax(${PROJECT_CARD_DESKTOP_MIN_WIDTH}, ${PROJECT_CARD_DESKTOP_MAX_WIDTH}))`,
    },
    gap: spacing.groupingDesktop,
  },
  projectsTitle: {
    mb: { xs: spacing.groupingMobile, lg: spacing.groupingDesktop },
  },
  rightContainer: {
    my: spacing.regularDesktop,
    mx: spacing.mediumDesktop,
    display: { xs: "none", lg: "block" },
    minWidth: "300px",
  },
});

const Team = () => {
  const outlet = useOutlet();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { teamId } = useParams();
  const [modalOpen, setModalOpen] = useState(false);
  const { isGloballyLoading } = useGlobalLoading();
  const { setIsAppLoading } = useAppLoading();
  const [isGeneratingTemplate, setIsGeneratingTemplate] = useState(false);
  const { team, isTeamLoading, refetchTeam, refetchTeams } = useTeam();

  const userInfo = useSelector((state) => state.userInfo);

  const addProject = useCallback(async () => {
    const bodyData = {
      baseName: t("project.project"),
      teamId: teamId ?? "",
    };

    const res = await ProjectService.createProject(bodyData);

    return res.data.data.id;
  }, [teamId, t]);

  const onRegenerateTemplateClick = useCallback(async () => {
    setIsGeneratingTemplate(true);

    try {
      TemplateService.regenerateTemplate(team?.id ?? "", {
        fireAndForget: true,
      });

      toast.success(t("templates.regenerate_template_started"));
      setIsGeneratingTemplate(false);

      await refetchTeams({ skipLoading: true });
    } catch (error) {
      console.error("Failed to start template regeneration:", error);
      toast.error(t("templates.regenerate_template_failed_to_start"));
      setIsGeneratingTemplate(false);
    }
  }, [t, refetchTeams, team?.id]);

  const onAddProjectClick = useCallback(async () => {
    setIsAppLoading(true);
    const projectId = await addProject();
    await refetchTeam({ skipLoading: true });
    await refetchTeams({ skipLoading: true });
    setIsAppLoading(false);
    navigate(generatePath(ROUTES.PROJECT, { projectId, teamId }));
  }, [
    setIsAppLoading,
    addProject,
    refetchTeam,
    refetchTeams,
    navigate,
    teamId,
  ]);

  useEffect(() => {
    refetchTeam({ skipLoading: teamId === team?.id });
  }, [refetchTeam, team?.id, teamId]);

  usePageTitle("pages.team.title", { id: team?.name });

  const isLoading = useMemo(
    () => isGloballyLoading || isTeamLoading || !team,
    [isGloballyLoading, isTeamLoading, team]
  );
  if (outlet) {
    return outlet;
  }

  return (
    // <OverlayScrollbarWrapper>
    <Box sx={styles.container}>
      <Box sx={styles.leftContainer}>
        <Box sx={styles.projectName}>
          <TeamTopSection
            canDeleteTeam={userInfo.canDeleteTeam}
            canRenameTeam={userInfo.canRenameTeam}
            team={team}
          />
        </Box>
        <InviteTeammateModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          teamId={teamId}
          projects={team?.projects || []}
        />
        <Box sx={styles.buttonContainer}>
          {userInfo.canAddProject && (
            <GradientButton
              text={t("navigation.new_project")}
              data-test="team.newProject"
              icon={"add"}
              onClick={onAddProjectClick}
              isLoading={!team || isLoading}
              textSx={{
                fontSize: { xs: "12px", lg: "16px" },
                display: "flex",
                lineHeight: { xs: "16px", lg: "auto" },
              }}
            />
          )}
          <GradientButton
            text={t("invite.invite_teammate")}
            data-test="team.inviteTeammate"
            icon={"person_add"}
            onClick={() => setModalOpen(true)}
            isLoading={!team || isLoading}
            textSx={{
              fontSize: { xs: "12px", lg: "16px" },
              display: "flex",
              lineHeight: { xs: "16px", lg: "auto" },
            }}
          />
          {team?.isSourceTemplate && (
            <GradientButton
              text={
                isGeneratingTemplate
                  ? t("templates.regenerate_template_loading")
                  : t("templates.regenerate_template")
              }
              icon={"refresh"}
              isLoading={isLoading}
              disabled={isGeneratingTemplate}
              onClick={onRegenerateTemplateClick}
              textSx={{
                fontSize: { xs: "12px", lg: "16px" },
                display: "flex",
                lineHeight: { xs: "16px", lg: "auto" },
              }}
            />
          )}
        </Box>
        <Box>
          <Typography sx={styles.projectsTitle} variant="h2">
            {isLoading ? (
              <Skeleton variant="text" width={150} />
            ) : (
              t("project.projects")
            )}
          </Typography>

          <Box sx={styles.projectContainer}>
            {isGloballyLoading || !team || isTeamLoading ? (
              <ProjectCardSkeleton />
            ) : (
              team?.projects?.map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))
            )}
          </Box>
        </Box>
      </Box>
      <Box sx={styles.rightContainer}>
        <Box>
          <Typography variant="h2" mb={spacing.groupingDesktop}>
            {isLoading ? (
              <Skeleton variant="text" width={150} />
            ) : (
              t("project.team")
            )}
          </Typography>
        </Box>
        <Box
          gap={"12px"}
          display="flex"
          flexDirection="column"
          maxWidth={"300px"}
        >
          {isLoading || !team ? (
            <ContentRepeater repeatCount={5}>
              <TeammateSkeleton />
            </ContentRepeater>
          ) : (
            <>
              {(team?.teammates ?? []).map((teammate) => {
                return (
                  <Teammate
                    userId={teammate.id}
                    first_name={teammate.firstName}
                    last_name={teammate.lastName}
                    email={teammate.email}
                    imageUrl={teammate.imageUrl}
                    canDelete={canDeleteUser(team)}
                    key={teammate.id}
                    teamId={team.id}
                    teammateUserId={teammate.id}
                    isCurrentUser={isCurrentUser(userInfo, teammate.id)}
                    isInternalTeamMember={teammate.isInternalTeamMember}
                  />
                );
              })}
              <TeammateBlankState onClick={() => setModalOpen(true)} />
            </>
          )}
        </Box>
      </Box>
    </Box>
    // </OverlayScrollbarWrapper>
  );
};

export default Team;
